import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
    UtensilsCrossed, Loader2, User, KeyRound, Delete, Shield
} from 'lucide-react';
import { cn } from '../lib/utils';

// ========================================
// PIN KEYPAD COMPONENT
// ========================================
const PinKeypad = ({ pin, setPin, maxLength, loading, disabled }) => {
    const handleKeyPress = (key) => {
        if (disabled) return;
        
        if (key === 'delete') {
            setPin(pin.slice(0, -1));
        } else if (key === 'clear') {
            setPin('');
        } else if (pin.length < maxLength) {
            setPin(pin + key);
        }
    };

    const keys = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['clear', '0', 'delete']
    ];

    return (
        <div className="space-y-4">
            {/* PIN Display */}
            <div className="flex justify-center gap-2 sm:gap-3 mb-4">
                {[...Array(maxLength)].map((_, i) => (
                    <div
                        key={i}
                        className={cn(
                            "w-9 h-11 sm:w-10 sm:h-12 rounded-xl border-2 flex items-center justify-center text-lg sm:text-xl font-bold transition-all",
                            i < pin.length 
                                ? "border-green-500 bg-green-50 text-green-700" 
                                : "border-gray-200 bg-gray-50"
                        )}
                    >
                        {i < pin.length ? '•' : ''}
                    </div>
                ))}
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-[280px] sm:max-w-[300px] mx-auto">
                {keys.flat().map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => handleKeyPress(key)}
                        disabled={loading || disabled}
                        className={cn(
                            "h-14 sm:h-16 rounded-xl font-bold text-xl sm:text-2xl transition-all active:scale-95 disabled:opacity-50 touch-target",
                            key === 'delete' 
                                ? "bg-red-100 text-red-600 hover:bg-red-200 active:bg-red-200" 
                                : key === 'clear'
                                ? "bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-200 text-sm sm:text-base"
                                : "bg-white border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 active:bg-green-100"
                        )}
                    >
                        {key === 'delete' ? (
                            <Delete className="w-5 h-5 sm:w-6 sm:h-6 mx-auto" />
                        ) : key === 'clear' ? (
                            'C'
                        ) : (
                            key
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

// ========================================
// LOGIN PAGE
// ========================================
const LoginPage = () => {
    const { loginWithPin, register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [canRegister, setCanRegister] = useState(false);
    const [checkingRegister, setCheckingRegister] = useState(true);
    // Login state
    const [username, setUsername] = useState('');
    const [pin, setPin] = useState('');
    const [step, setStep] = useState('username'); // 'username' | 'pin'
    const [pinLength, setPinLength] = useState(4);
    const [displayName, setDisplayName] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    
    // Register form
    const [view, setView] = useState('login'); // 'login' | 'register'
    const [regName, setRegName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');

    useEffect(() => {
        let mounted = true;
        
        const checkCanRegister = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/can-register`);
                const data = await response.json();
                if (mounted) {
                    setCanRegister(data.can_register);
                    if (data.can_register) {
                        setView('register');
                    }
                }
            } catch (error) {
                if (mounted) {
                    setCanRegister(false);
                }
            } finally {
                if (mounted) {
                    setCheckingRegister(false);
                }
            }
        };
        
        checkCanRegister();
        
        return () => {
            mounted = false;
        };
    }, []);

    // Handle username submission - check PIN length
    const handleUsernameSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim()) return;
        
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/pin-length/${encodeURIComponent(username.trim())}`
            );
            
            if (response.ok) {
                const data = await response.json();
                setPinLength(data.pin_length);
                setDisplayName(data.name);
                setIsAdmin(data.pin_length === 6);
                setStep('pin');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Utilisateur non trouvé');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    // Handle PIN login
    const handlePinLogin = async () => {
        if (pin.length !== pinLength) return;
        
        setLoading(true);
        try {
            await loginWithPin(username, pin);
            toast.success('Connexion réussie');
            // La redirection sera gérée par AuthenticatedRedirect dans App.js
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(detail || 'Erreur de connexion');
            setPin('');
        } finally {
            setLoading(false);
        }
    };

    // Go back to username step
    const handleBack = () => {
        setStep('username');
        setPin('');
        setPinLength(4);
        setIsAdmin(false);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(regName, regEmail, regPassword);
            toast.success('Compte créé');
            // La redirection sera gérée par AuthenticatedRedirect dans App.js
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    // Render username input step
    const renderUsernameStep = () => (
        <CardContent className="p-6">
            <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-display text-xl font-semibold">Connexion</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Entrez votre pseudo
                </p>
            </div>
            
            <form onSubmit={handleUsernameSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="username">Pseudo</Label>
                    <Input
                        id="username"
                        type="text"
                        placeholder="Votre pseudo..."
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        autoFocus
                        className="text-lg h-12 text-center"
                        data-testid="login-username-input"
                    />
                </div>
                <Button 
                    type="submit" 
                    className="w-full h-12 bg-green-600 hover:bg-green-700 text-lg"
                    disabled={!username.trim() || loading}
                    data-testid="login-next-btn"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Vérification...
                        </>
                    ) : (
                        'Suivant'
                    )}
                </Button>
            </form>
        </CardContent>
    );

    // Render PIN input step
    const renderPinStep = () => (
        <CardContent className="p-6">
            <div className="text-center mb-4">
                <button
                    type="button"
                    onClick={handleBack}
                    className="text-sm text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 mx-auto"
                >
                    ← Changer de pseudo
                </button>
                <div className={cn(
                    "w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4",
                    isAdmin ? "bg-purple-100" : "bg-green-100"
                )}>
                    {isAdmin ? (
                        <Shield className="w-8 h-8 text-purple-600" />
                    ) : (
                        <KeyRound className="w-8 h-8 text-green-600" />
                    )}
                </div>
                <h2 className="font-display text-xl font-semibold">
                    Bonjour, {displayName || username}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    {isAdmin ? (
                        <span className="text-purple-600 font-medium">Admin - Code PIN 6 chiffres</span>
                    ) : (
                        'Entrez votre code PIN'
                    )}
                </p>
            </div>
            
            <PinKeypad 
                pin={pin}
                setPin={setPin}
                maxLength={pinLength}
                loading={loading}
                disabled={loading}
            />
            
            <Button 
                onClick={handlePinLogin}
                className={cn(
                    "w-full h-12 text-lg mt-4",
                    isAdmin 
                        ? "bg-purple-600 hover:bg-purple-700" 
                        : "bg-green-600 hover:bg-green-700"
                )}
                disabled={pin.length !== pinLength || loading}
                data-testid="login-submit-btn"
            >
                {loading ? (
                    <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Connexion...
                    </>
                ) : (
                    'Se connecter'
                )}
            </Button>
        </CardContent>
    );

    // Render register view
    const renderRegister = () => (
        <CardContent className="p-6">
            <div className="text-center mb-6">
                <h2 className="font-display text-xl font-semibold">Créer un compte Admin</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Premier compte administrateur
                </p>
            </div>
            <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="reg-name">Nom complet</Label>
                    <Input
                        id="reg-name"
                        type="text"
                        placeholder="Votre nom"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        required
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                        id="reg-email"
                        type="email"
                        placeholder="admin@restaurant.com"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        required
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="reg-password">Mot de passe</Label>
                    <Input
                        id="reg-password"
                        type="password"
                        placeholder="Minimum 6 caractères"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        required
                        minLength={6}
                    />
                </div>
                <Button 
                    type="submit" 
                    className="w-full bg-accent hover:bg-accent/90"
                    disabled={loading}
                >
                    {loading ? 'Création...' : 'Créer le compte'}
                </Button>
            </form>
        </CardContent>
    );

    if (checkingRegister) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4 safe-area-bottom">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-6 sm:mb-8">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                        <UtensilsCrossed className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight">ALDRIN'S</h1>
                    <p className="text-muted-foreground text-xs sm:text-sm">Restaurant POS</p>
                </div>

                <Card className="shadow-xl border-0">
                    {view === 'register' ? (
                        renderRegister()
                    ) : step === 'username' ? (
                        renderUsernameStep()
                    ) : (
                        renderPinStep()
                    )}
                </Card>

                <p className="text-center text-[10px] sm:text-xs text-muted-foreground mt-4 sm:mt-6">
                    Personnel : PIN 4 chiffres • Admin : PIN 6 chiffres
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
